import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import "chart.js/auto";
import { Bar, Chart } from "react-chartjs-2";

import CustomSelect from "../../Components/common/CustomSelect/CustomSelect";
import TableSpinnerIcon from "../../Components/Icon-SVG/TableSpinnerIcon";
import {
  DoughnutWithLegend,
  KpiTile,
  NoData,
  RankList,
  Widget,
  percent,
  useThemeColors,
} from "../../Components/common/DashboardWidgets/DashboardWidgets";
import SankeyChart from "./SankeyChart";
import { exportDashboardHtml, exportDashboardPdf } from "./exportDashboard";

import {
  RequestDashboardData,
  RequestDashboardHealth,
  RequestIndexes,
} from "../../store/actions/zsearchActions";
import {
  RequestAptAnalysisStatus,
  RequestDashboardInsights,
} from "../../store/actions/mailGatewayActions";
import { isEmpty } from "./util";

import "./styles.scss";

const VERDICT_COLORS = {
  Malicious: "#e65555",
  Suspicious: "#e69b55",
  Spam: "#a77bdb",
  Clean: "#58c86b",
};

const SANKEY_COLORS = {
  ...VERDICT_COLORS,
  "Inbound Emails": "#36a2eb",
  "Attachment Analysis": "#5b8def",
  "Link Analysis": "#3fb6c9",
  "Spam Filter": "#8f86e8",
  "Content Scan": "#7f8aa3",
};

const DELIVERY_COLORS = {
  sent: "#36a2eb",
  softbounce: "#e6c255",
  "not delivered": "#e65555",
  rejected: "#c94a4a",
  dropped: "#e69b55",
  deferred: "#b0b6c6",
  bounced: "#a77bdb",
  blocked: "#6c7a96",
};

const APT_STATUS_COLORS = {
  blocked: "#e65555",
  unblocked: "#58c86b",
  analysed: "#36a2eb",
  released: "#e6c255",
};

const LINK_COLORS = {
  Clean: VERDICT_COLORS.Clean,
  Suspicious: VERDICT_COLORS.Suspicious,
  Malicious: VERDICT_COLORS.Malicious,
};

const FALLBACK_COLOR = "#7f8aa3";

const HEALTH_CHECKS = [
  ["postfix", "Mail Daemon"],
  ["spamd", "Spam Filter"],
  ["content_filter", "Content Filter"],
  ["fast_analyser", "Fast Analyzer"],
  ["disk", "Disk Space"],
  ["db_status", "Config DB"],
  ["fti_status", "FTI Access"],
];

// Today and the 7 days before it; the backend buckets days in the browser's
// time zone with the same YYYY-MM-DD keys.
const lastDays = () =>
  Array.from({ length: 8 }, (_, i) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - (7 - i));
    return date;
  });

const isoDayKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const formatDateTime = (value) => {
  const date = new Date(value);
  if (isNaN(date)) return value || "-";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const StatusIndicator = ({ label, value }) => (
  <div className="mgw-health__item">
    <b className={`system-health__block--signal--status ${value ? "open" : "close"}`}></b>
    <span>{label}</span>
  </div>
);

const Dashboard = () => {
  const dispatch = useDispatch();
  const theme = useThemeColors();

  const dashboardRef = useRef(null);
  const [selectedHost, setSelectedHost] = useState(null);
  const [exporting, setExporting] = useState(null);

  const mgwDevices = useSelector((state) => state.zsearch.mgwDevices);
  const mgwDevicesLoading = useSelector((state) => state.zsearch.mgwDevicesLoading);
  const data = useSelector((state) => state.zsearch.dashboardData);
  const alerts = useSelector((state) => state.zsearch.dashboardAlerts);
  const dataLoading = useSelector((state) => state.zsearch.dashboardDataLoading);
  const aptAnalysisStatus = useSelector((state) => state.mailGateway.aptAnalysisStatus);
  const aptAnalysisStatusLoading = useSelector(
    (state) => state.mailGateway.aptAnalysisStatusLoading,
  );
  const insights = useSelector((state) => state.mailGateway.dashboardInsights);
  const insightsLoading = useSelector((state) => state.mailGateway.dashboardInsightsLoading);
  const healthDataLoading = useSelector((state) => state.zsearch.dashboardHealthLoading);

  useEffect(() => {
    dispatch(RequestIndexes("postfix."));
  }, [dispatch]);

  const loadDashboard = () => {
    if (selectedHost?.id) {
      dispatch(RequestDashboardData(selectedHost.id));
      dispatch(RequestAptAnalysisStatus(selectedHost.id));
      dispatch(RequestDashboardInsights(selectedHost.id));
      dispatch(RequestDashboardHealth(selectedHost.id));
    }
  };

  useEffect(loadDashboard, [dispatch, selectedHost]); // eslint-disable-line react-hooks/exhaustive-deps

  const kpis = insights.kpis || {};
  const links = insights.links || {};
  const failures = data.failures;
  const avgDelay = Number(data.avgDelay) || 0;
  const failureRate = `${failures.approximate ? "~" : ""}${Number(failures.rate).toFixed(1)}`;
  const healthTotal = HEALTH_CHECKS.filter(([key]) => key in data.procHealth).length;
  const healthUp = HEALTH_CHECKS.filter(([key]) => data.procHealth[key]).length;

  const axisOptions = useMemo(
    () => ({
      ticks: { color: theme.text, font: { size: 11 } },
      grid: { color: theme.grid, drawTicks: false },
      border: { display: false },
    }),
    [theme],
  );

  const trendChart = useMemo(() => {
    const insightsByDay = {};
    (insights.daily || []).forEach((day) => {
      insightsByDay[day.date] = day;
    });
    const days = lastDays();
    const keys = days.map(isoDayKey);
    const threatDataset = (verdict) => ({
      type: "bar",
      label: verdict,
      data: keys.map((key) => insightsByDay[key]?.[verdict] || 0),
      backgroundColor: VERDICT_COLORS[verdict],
      stack: "threats",
      yAxisID: "threats",
      borderRadius: 3,
      maxBarThickness: 26,
      order: 2,
    });

    return {
      data: {
        labels: days.map((day) =>
          day.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        ),
        datasets: [
          {
            type: "line",
            label: "Emails received",
            data: keys.map((key) => data.emailVolume[key] || 0),
            borderColor: "#36a2eb",
            backgroundColor: "rgba(54, 162, 235, 0.15)",
            fill: true,
            tension: 0.35,
            pointRadius: 3,
            yAxisID: "volume",
            order: 1,
          },
          threatDataset("Malicious"),
          threatDataset("Suspicious"),
          threatDataset("Spam"),
        ],
      },
      options: {
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            position: "bottom",
            labels: { color: theme.text, boxWidth: 12, usePointStyle: true },
          },
        },
        scales: {
          x: { ...axisOptions, stacked: true, grid: { display: false } },
          volume: {
            ...axisOptions,
            position: "left",
            beginAtZero: true,
            title: { display: true, text: "Emails", color: theme.text },
          },
          threats: {
            ...axisOptions,
            position: "right",
            stacked: true,
            beginAtZero: true,
            grid: { display: false },
            title: { display: true, text: "Threats", color: theme.text },
          },
        },
      },
    };
  }, [insights.daily, data.emailVolume, axisOptions, theme]);

  const horizontalBarOptions = useMemo(
    () => ({
      indexAxis: "y",
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { ...axisOptions, beginAtZero: true },
        y: { ...axisOptions, grid: { display: false } },
      },
    }),
    [axisOptions],
  );

  const topSenderDomains = data.senderDomains;
  // Counted from the newest records when the index cannot aggregate whole addresses
  const senderDomainsSubtitle = data.senderDomainsSample
    ? `Latest ${data.senderDomainsSample.toLocaleString()} records`
    : "All inbound mail";

  const attachmentTypes = insights.attachment_types || [];
  const attachmentChart = {
    labels: attachmentTypes.map((type) => `.${type.name}`),
    datasets: [
      {
        label: "Malicious",
        data: attachmentTypes.map((type) => type.malicious),
        backgroundColor: VERDICT_COLORS.Malicious,
        stack: "files",
        borderRadius: 3,
      },
      {
        label: "Other",
        data: attachmentTypes.map((type) => type.count - type.malicious),
        backgroundColor: "#5b8def",
        stack: "files",
        borderRadius: 3,
      },
    ],
  };

  const reasons = insights.reasons || [];
  const reasonChart = {
    labels: reasons.map((reason) => reason.name),
    datasets: [
      {
        data: reasons.map((reason) => reason.count),
        backgroundColor: reasons.map((reason) =>
          /APT/.test(reason.name)
            ? VERDICT_COLORS.Malicious
            : /SPAM/.test(reason.name)
              ? VERDICT_COLORS.Spam
              : /Blocked/.test(reason.name)
                ? VERDICT_COLORS.Suspicious
                : VERDICT_COLORS.Clean,
        ),
        borderRadius: 3,
        maxBarThickness: 18,
      },
    ],
  };

  const hourlyVolume = data.hourlyVolume || [];
  const hourlyChart = {
    labels: Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, "0")),
    datasets: [
      {
        label: "Emails",
        data: Array.from({ length: 24 }, (_, hour) => hourlyVolume[hour] || 0),
        backgroundColor: "rgba(54, 162, 235, 0.75)",
        borderRadius: 3,
      },
    ],
  };

  const deliveryItems = Object.entries(data.statusCounts)
    .sort(([, a], [, b]) => b - a)
    .map(([label, value]) => ({
      label,
      value,
      color: DELIVERY_COLORS[label] || FALLBACK_COLOR,
    }));

  const aptItems = ["blocked", "unblocked", "released"]
    .filter((key) => aptAnalysisStatus[key] !== undefined)
    .map((key) => ({
      label: key,
      value: aptAnalysisStatus[key],
      color: APT_STATUS_COLORS[key],
      inChart: key !== "released",
    }));

  const linkItems = ["Malicious", "Suspicious", "Clean"].map((verdict) => ({
    label: verdict,
    value: links[verdict] || 0,
    color: LINK_COLORS[verdict],
  }));

  const sankey = insights.sankey || { nodes: [], links: [] };
  const isLoading = dataLoading || aptAnalysisStatusLoading || healthDataLoading || insightsLoading;
  const exportHost = selectedHost?.value || selectedHost?.id || "";

  const exportHandler = async (format) => {
    if (!dashboardRef.current || exporting) return;
    setExporting(format);
    try {
      if (format === "pdf") {
        await exportDashboardPdf(dashboardRef.current, exportHost);
      } else {
        exportDashboardHtml(dashboardRef.current, exportHost);
      }
    } catch (e) {
      console.error("Dashboard export failed:", e);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="mgw-dashboard" ref={dashboardRef}>
      <div className="mgw-dashboard__header">
        <h1>Dashboard</h1>
        <p>Mail flow and threat analysis of the selected host for the last 7 days.</p>
        <div className="mgw-dashboard__header__select">
          <p>Select host: </p>
          <CustomSelect
            id="mgwDashboard"
            data={mgwDevices.length ? mgwDevices : []}
            select={selectedHost}
            setSelected={setSelectedHost}
            width={220}
            disabled={mgwDevices.length === 0}
            placeholder={mgwDevicesLoading ? "Fetching data..." : "No data."}
          />
          <div className="mgw-dashboard__refresh" onClick={loadDashboard} title="Refresh">
            <TableSpinnerIcon
              className="container-services__spinner"
              customStyle={{ margin: "0", height: "20px" }}
              spin={isLoading}
            />
          </div>
        </div>
      </div>

      <div className="mgw-dashboard__grid">
        <div className="mgw-dashboard__kpis">
          <KpiTile
            label="Emails Processed"
            value={data.totalEmails.toLocaleString()}
            hint="MTA log entries"
          />
          <KpiTile
            label="Analysed"
            value={(kpis.analysed ?? 0).toLocaleString()}
            hint={`${(kpis.attachments ?? 0).toLocaleString()} attachments`}
            tone="info"
          />
          <KpiTile
            label="Threats Blocked"
            value={(kpis.blocked ?? 0).toLocaleString()}
            hint={`${percent(kpis.blocked, kpis.analysed)}% of analysed`}
            tone="danger"
          />
          <KpiTile
            label="APT Detected"
            value={(kpis.apt ?? 0).toLocaleString()}
            hint={`${(kpis.malicious_attachments ?? 0).toLocaleString()} malicious files`}
            tone="danger"
          />
          <KpiTile label="Spam Blocked" value={(kpis.spam ?? 0).toLocaleString()} tone="spam" />
          <KpiTile
            label="Malicious Links"
            value={(kpis.malicious_links ?? 0).toLocaleString()}
            hint={`${(links.total ?? 0).toLocaleString()} links analysed`}
            tone="warning"
          />
          <KpiTile
            label="Released"
            value={(kpis.released ?? 0).toLocaleString()}
            hint="Released by admins"
          />
          <KpiTile
            label="Avg. Delay"
            value={`${avgDelay.toFixed(2)}s`}
            hint={`${failureRate}% not delivered`}
            tone={avgDelay > 100 ? "warning" : "default"}
          />
        </div>

        <Widget
          title="Mail Threat Flow"
          subtitle="Inbound emails by analysis type and verdict. The link flow counts analysed URLs."
          span={8}
        >
          {sankey.nodes.length ? (
            <SankeyChart
              nodes={sankey.nodes}
              links={sankey.links}
              colors={SANKEY_COLORS}
              unitOf={(band) =>
                band.source === "Link Analysis" || band.target === "Link Analysis"
                  ? "links"
                  : "emails"
              }
            />
          ) : (
            <NoData />
          )}
        </Widget>

        <Widget
          title="APT Analysis Status"
          subtitle="Emails sent to APT analysis in the last 7 days"
          span={4}
          className="mgw-dashboard__item--centered"
        >
          {isEmpty(aptAnalysisStatus) ? (
            <NoData />
          ) : (
            <>
              <DoughnutWithLegend
                items={aptItems}
                centerValue={(aptAnalysisStatus.analysed ?? 0).toLocaleString()}
                centerLabel="analysed"
              />
              <dl className="mgw-definitions">
                <div>
                  <dt>Analysed</dt>
                  <dd>
                    Every email checked by APT analysis: blocked plus unblocked emails together.
                  </dd>
                </div>
                <div>
                  <dt>Released</dt>
                  <dd>
                    Blocked emails an admin delivered to the recipient with the Release button.
                  </dd>
                </div>
              </dl>
            </>
          )}
        </Widget>

        <Widget title="Alerts" subtitle="Delay, volume and rejection rate thresholds" span={8}>
          {alerts.length > 0 ? (
            <ul className="mgw-alerts">
              {alerts.map((alert, index) => (
                <li key={index} className="mgw-alerts__item">
                  {alert}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mgw-alerts__empty">No alerts triggered.</p>
          )}
        </Widget>

        <Widget
          title="Gateway Status"
          subtitle="Service health and mail delivery of the selected host"
          span={4}
          className="mgw-dashboard__item--tall"
          action={
            healthTotal > 0 && (
              <span
                className={`mgw-status__chip ${healthUp === healthTotal ? "mgw-status__chip--ok" : "mgw-status__chip--down"}`}
              >
                {healthUp === healthTotal
                  ? "All services up"
                  : `${healthTotal - healthUp} service${healthTotal - healthUp > 1 ? "s" : ""} down`}
              </span>
            )
          }
        >
          <div className="mgw-status">
            <div className="mgw-status__section">
              <h3>Service Health</h3>
              <p className="mgw-status__info">
                {healthTotal
                  ? `${healthUp} of ${healthTotal} gateway services running`
                  : "No health data received"}
              </p>
              <div className="mgw-health">
                {HEALTH_CHECKS.map(([key, label]) => (
                  <StatusIndicator key={key} label={label} value={data.procHealth[key]} />
                ))}
              </div>
            </div>
            <div className="mgw-status__section">
              <h3>Delivery Status</h3>
              <p className="mgw-status__info">
                MTA delivery results · {failureRate}% not delivered
                {data.blockedHeld
                  ? ` · ${data.blockedHeld.toLocaleString()} blocked by the gateway`
                  : ""}
              </p>
              {deliveryItems.length ? (
                <DoughnutWithLegend
                  items={deliveryItems}
                  centerValue={data.totalEmails.toLocaleString()}
                  centerLabel="emails"
                />
              ) : (
                <NoData />
              )}
            </div>
          </div>
        </Widget>

        <Widget title="Email Volume & Threats" subtitle="Per day" span={8}>
          {isEmpty(data.emailVolume) && !insights.daily?.length ? (
            <NoData />
          ) : (
            <div className="mgw-chart mgw-chart--tall">
              <Chart type="bar" data={trendChart.data} options={trendChart.options} />
            </div>
          )}
        </Widget>

        <Widget title="Top Sender Domains" subtitle={senderDomainsSubtitle} span={4}>
          <RankList items={topSenderDomains} color="#36a2eb" />
        </Widget>

        <Widget
          title="Top Threat Sources"
          subtitle="Sender domains of spam and malicious mail"
          span={4}
          action={
            <Link
              className="mgw-dashboard__link mgw-export-ignore"
              to="/mailgateway/blocked-emails"
            >
              View blocked
            </Link>
          }
        >
          <RankList
            items={insights.top_threat_senders}
            color={VERDICT_COLORS.Malicious}
            emptyText="No threats detected."
          />
        </Widget>

        <Widget title="Most Targeted Recipients" subtitle="Recipients of blocked mail" span={4}>
          <RankList
            items={insights.top_targeted_recipients}
            color={VERDICT_COLORS.Suspicious}
            emptyText="No targeted recipients."
          />
        </Widget>

        <Widget title="Analysis Decisions" subtitle="Why mail was blocked or let through" span={4}>
          {reasons.length ? (
            <div className="mgw-chart">
              <Bar data={reasonChart} options={horizontalBarOptions} />
            </div>
          ) : (
            <NoData />
          )}
        </Widget>

        <Widget title="Attachment Types" subtitle="Top extensions analysed" span={4}>
          {attachmentTypes.length ? (
            <div className="mgw-chart">
              <Bar
                data={attachmentChart}
                options={{
                  ...horizontalBarOptions,
                  plugins: {
                    legend: {
                      display: true,
                      position: "bottom",
                      labels: { color: theme.text, boxWidth: 12 },
                    },
                  },
                  scales: {
                    x: { ...horizontalBarOptions.scales.x, stacked: true },
                    y: { ...horizontalBarOptions.scales.y, stacked: true },
                  },
                }}
              />
            </div>
          ) : (
            <NoData text="No attachments analysed." />
          )}
        </Widget>

        <Widget
          title="Link Analysis"
          subtitle={`${(links.visited ?? 0).toLocaleString()} links visited by recipients`}
          span={4}
          action={
            <Link
              className="mgw-dashboard__link mgw-export-ignore"
              to="/mailgateway/dynamic-phishing-emails"
            >
              View links
            </Link>
          }
        >
          {links.total ? (
            <>
              <DoughnutWithLegend
                items={linkItems}
                centerValue={links.total.toLocaleString()}
                centerLabel="links"
              />
              {links.top_malicious_domains?.length > 0 && (
                <>
                  <h3 className="mgw-dashboard__subheading">Malicious link domains</h3>
                  <RankList items={links.top_malicious_domains} color={VERDICT_COLORS.Malicious} />
                </>
              )}
            </>
          ) : (
            <NoData text="No links analysed." />
          )}
        </Widget>

        <Widget title="Traffic by Hour" subtitle="Emails received per hour of day" span={4}>
          {data.totalEmails ? (
            <div className="mgw-chart">
              <Bar
                data={hourlyChart}
                options={{
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { ...axisOptions, grid: { display: false } },
                    y: { ...axisOptions, beginAtZero: true },
                  },
                }}
              />
            </div>
          ) : (
            <NoData />
          )}
        </Widget>

        <Widget
          title="Recent Threats"
          subtitle="Latest spam and malicious mail"
          span={8}
          action={
            <Link
              className="mgw-dashboard__link mgw-export-ignore"
              to="/mailgateway/blocked-emails"
            >
              View all
            </Link>
          }
        >
          {insights.recent_threats?.length ? (
            <div className="mgw-table mgw-table--compact">
              <table>
                <colgroup>
                  <col style={{ width: "104px" }} />
                  <col style={{ width: "24%" }} />
                  <col style={{ width: "22%" }} />
                  <col />
                  <col style={{ width: "170px" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Sender</th>
                    <th>Recipients</th>
                    <th>Subject</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {insights.recent_threats.slice(0, 10).map((threat, index) => (
                    <tr key={index}>
                      <td>{formatDateTime(threat.date)}</td>
                      <td title={threat.sender}>{threat.sender || "-"}</td>
                      <td title={threat.recipients}>{threat.recipients || "-"}</td>
                      <td title={threat.subject}>{threat.subject || "-"}</td>
                      <td>
                        <span
                          className={`mgw-badge mgw-badge--${threat.verdict.toLowerCase()}`}
                          title={threat.released ? "Released" : threat.reason}
                        >
                          {threat.reason}
                          {threat.released ? " · released" : ""}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <NoData text="No threats detected." />
          )}
        </Widget>

        <Widget
          title="Delivery Failures"
          subtitle={`${failures.approximate ? "~" : ""}${failures.count.toLocaleString()} bounced, rejected or not delivered (${failureRate}%), gateway blocks excluded · Average delivery delay ${avgDelay.toFixed(2)} seconds`}
          span={8}
          className="mgw-export-full"
        >
          {failures.items.length ? (
            <div className="mgw-table mgw-table--compact">
              <table>
                <colgroup>
                  <col style={{ width: "104px" }} />
                  <col style={{ width: "26%" }} />
                  <col style={{ width: "24%" }} />
                  <col />
                  <col style={{ width: "104px" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>From Address</th>
                    <th>To Address</th>
                    <th>Subject</th>
                    <th>State</th>
                  </tr>
                </thead>
                <tbody>
                  {failures.items.slice(0, 15).map((log, index) => (
                    <tr key={log.queue_id || index}>
                      <td>{formatDateTime(log.date)}</td>
                      <td title={log.from_address}>{log.from_address || "-"}</td>
                      <td title={log.to_address}>{log.to_address || "-"}</td>
                      <td title={log.subject}>{log.subject || "-"}</td>
                      <td>
                        <span className={`mgw-badge mgw-badge--state-${log.status}`}>
                          {log.status === "timeout" ? "not delivered" : log.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <NoData text="No delivery failures." />
          )}
        </Widget>

        <Widget
          title="Export Report"
          subtitle="Save a snapshot of this dashboard to share or archive"
          span={4}
          className="mgw-export-ignore"
        >
          <div className="mgw-export">
            <div className="mgw-export__option">
              <div>
                <h3>PDF document</h3>
                <p>Every widget on a single page, ready to attach to a report or email.</p>
              </div>
              <button
                type="button"
                className="mgw-export__button"
                disabled={!exportHost || isLoading || Boolean(exporting)}
                onClick={() => exportHandler("pdf")}
              >
                {exporting === "pdf" ? "Preparing…" : "Download PDF"}
              </button>
            </div>
            <div className="mgw-export__option">
              <div>
                <h3>HTML page</h3>
                <p>A self-contained page that opens in any browser, no login needed.</p>
              </div>
              <button
                type="button"
                className="mgw-export__button"
                disabled={!exportHost || isLoading || Boolean(exporting)}
                onClick={() => exportHandler("html")}
              >
                {exporting === "html" ? "Preparing…" : "Download HTML"}
              </button>
            </div>
          </div>
        </Widget>
      </div>
    </div>
  );
};

export default Dashboard;
